import type { Category } from '@tanko/shared'
import type { CloudCategory } from './api-client'

/**
 * 本地分類遷移到雲端目錄的對應計算。
 *
 * 純函式：預覽與實際套用呼叫同一份對應，因此畫面上看到的就是會寫進去的。
 * 合併是不可逆的，而看不到的那幾筆正是會出事的地方，所以預覽不抽樣、不截斷。
 */

export type MigrationAction = 'merge' | 'create' | 'invalid'

export interface CategoryMigrationEntry {
  /** 本地分類識別碼。 */
  localId: string
  /** 本地分類名稱，原樣呈現（含前後空白），讓使用者認得出是哪一筆。 */
  localName: string
  action: MigrationAction
  /** 套用後在雲端的名稱。合併時是既有雲端分類的名稱，新建時是去空白後的本地名稱。 */
  cloudName: string
  /** 合併到既有雲端分類時的識別碼；新建與無效時沒有。 */
  cloudId?: string
  /** 與前面某一筆本地分類合併時，那一筆的識別碼。用來說明「不會建立重複」。 */
  mergesWithLocalId?: string
  /** 受影響的作品數。 */
  comicCount: number
}

/** 比對前去除前後空白並忽略大小寫：拉丁文字的 Hunter 與 hunter 視為同一個分類。 */
const normalise = (name: string) => name.trim().toLocaleLowerCase()

export function planCategoryMigration(
  localCategories: readonly Category[],
  cloudCategories: readonly Pick<CloudCategory, 'id' | 'name'>[],
  comicCountOf: (localCategoryId: string) => number,
): CategoryMigrationEntry[] {
  const existing = new Map(cloudCategories.map(category => [normalise(category.name), category]))
  // 同一批內同名的本地分類指向第一筆，否則遷移本身就會製造出重複分類。
  const plannedLocally = new Map<string, { localId: string; name: string }>()
  return localCategories.map(category => {
    const trimmed = category.name.trim()
    const base = { localId: category.id, localName: category.name, comicCount: comicCountOf(category.id) }
    if (!trimmed) return { ...base, action: 'invalid' as const, cloudName: '' }
    const key = normalise(category.name)
    const cloud = existing.get(key)
    // 既有雲端分類優先：合併到它，並採用它的名稱。
    if (cloud) return { ...base, action: 'merge' as const, cloudName: cloud.name, cloudId: cloud.id }
    const planned = plannedLocally.get(key)
    if (planned) return { ...base, action: 'merge' as const, cloudName: planned.name, mergesWithLocalId: planned.localId }
    plannedLocally.set(key, { localId: category.id, name: trimmed })
    return { ...base, action: 'create' as const, cloudName: trimmed }
  })
}

/** 預覽摘要：套用按鈕的標籤帶上實際會寫入的筆數。 */
export function summariseMigration(plan: readonly CategoryMigrationEntry[]) {
  return {
    create: plan.filter(entry => entry.action === 'create').length,
    merge: plan.filter(entry => entry.action === 'merge').length,
    invalid: plan.filter(entry => entry.action === 'invalid').length,
  }
}

/**
 * 套用遷移：建立需要的雲端分類，再把本地的分類識別碼與指派改寫成雲端識別碼。
 *
 * 可重入——已建立的分類在下一次執行時會因為同名而被判為合併，因此不會產生重複。
 * 寫入順序是「先雲端、後本地」：雲端失敗就不動本地，本地因此不會指向不存在的分類。
 */
export interface MigrationWriter {
  createCategory: (name: string) => Promise<{ id: string }>
  /** 把一本作品在雲端的分類指派設為這一組。 */
  setComicCategories: (catalogId: string, categoryIds: string[]) => Promise<void>
}

export interface ResolvedMigration {
  /** 本地分類識別碼 → 雲端分類識別碼。 */
  cloudIdByLocalId: Map<string, string>
  /** 因為名稱是空的而未遷移的本地分類。 */
  skipped: string[]
}

export async function applyCategoryMigration(
  plan: readonly CategoryMigrationEntry[], writer: Pick<MigrationWriter, 'createCategory'>,
): Promise<ResolvedMigration> {
  const cloudIdByLocalId = new Map<string, string>()
  const skipped: string[] = []
  for (const entry of plan) {
    if (entry.action === 'invalid') { skipped.push(entry.localId); continue }
    if (entry.action === 'merge') {
      // 合併到既有雲端分類，或合併到這一批稍早已建立的那一筆。
      const target = entry.cloudId ?? (entry.mergesWithLocalId ? cloudIdByLocalId.get(entry.mergesWithLocalId) : undefined)
      if (!target) throw new Error(`分類「${entry.localName}」找不到要合併的目標。`)
      cloudIdByLocalId.set(entry.localId, target)
      continue
    }
    const created = await writer.createCategory(entry.cloudName)
    cloudIdByLocalId.set(entry.localId, created.id)
  }
  return { cloudIdByLocalId, skipped }
}

/** 把本地指派的分類識別碼換成雲端識別碼，並去掉指向未遷移分類的那些。 */
export function remapAssignments(
  assignments: readonly { comicId: string; categoryId: string }[], cloudIdByLocalId: ReadonlyMap<string, string>,
): Map<string, string[]> {
  const byComic = new Map<string, string[]>()
  for (const assignment of assignments) {
    const cloudId = cloudIdByLocalId.get(assignment.categoryId)
    if (!cloudId) continue
    const current = byComic.get(assignment.comicId) ?? []
    if (!current.includes(cloudId)) current.push(cloudId)
    byComic.set(assignment.comicId, current)
  }
  return byComic
}

/** 遷移的三個步驟各自的進度，讓中斷時能說清楚停在哪一步。 */
export type MigrationStage = 'categories' | 'assignments' | 'done'
